import { Capacitor } from "@capacitor/core";

export interface PersistentStorage {
  getItem<T>(key: string): Promise<T | null>;
  setItem<T>(key: string, value: T): Promise<void>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
}

type StorageFactory = () => Promise<PersistentStorage>;

let cachedStorage: PersistentStorage | null = null;

/**
 * Returns a singleton persistent storage adapter that works across web + native.
 * Chooses IndexedDB/localforage on web and SQLite (with Preferences fallback) on native platforms.
 */
export const getPersistentStorage: StorageFactory = async () => {
  if (cachedStorage) {
    return cachedStorage;
  }

  if (Capacitor.isNativePlatform()) {
    cachedStorage = await createNativeStorage();
  } else {
    cachedStorage = await createWebStorage();
  }

  return cachedStorage;
};

/**
 * Factory used in tests to inject a deterministic in-memory adapter.
 */
export const createInMemoryStorage = (): PersistentStorage => {
  const store = new Map<string, unknown>();
  return {
    async getItem<T>(key: string): Promise<T | null> {
      return store.has(key) ? (store.get(key) as T) : null;
    },
    async setItem<T>(key: string, value: T): Promise<void> {
      store.set(key, value);
    },
    async removeItem(key: string): Promise<void> {
      store.delete(key);
    },
    async clear(): Promise<void> {
      store.clear();
    },
  };
};

const createWebStorage: StorageFactory = async () => {
  const localforage = await import("localforage");
  const instance = localforage.default.createInstance({
    name: "cabinet-logger",
    storeName: "offline-sync",
    description: "Offline cache + queue for Cabinet Logger",
  });
  return {
    async getItem<T>(key: string): Promise<T | null> {
      return (await instance.getItem<T>(key)) ?? null;
    },
    async setItem<T>(key: string, value: T): Promise<void> {
      await instance.setItem(key, value);
    },
    async removeItem(key: string): Promise<void> {
      await instance.removeItem(key);
    },
    async clear(): Promise<void> {
      await instance.clear();
    },
  };
};

const SQLITE_DB_NAME = "cabinet_logger_offline";
const SQLITE_TABLE = "kv_store";

const createNativeStorage: StorageFactory = async () => {
  try {
    const sqliteModule = await import("@capacitor-community/sqlite");
    const sqlite = sqliteModule.CapacitorSQLite as any;

    if (sqlite?.createConnection) {
      const dbConnection = await sqlite.createConnection(
        SQLITE_DB_NAME,
        false,
        "no-encryption",
        1,
        false
      );
      await dbConnection.open();
      await dbConnection.execute(
        `CREATE TABLE IF NOT EXISTS ${SQLITE_TABLE} (key TEXT PRIMARY KEY, value TEXT NOT NULL)`
      );

      return {
        async getItem<T>(key: string): Promise<T | null> {
          const result = await dbConnection.query(
            `SELECT value FROM ${SQLITE_TABLE} WHERE key = ?`,
            [key]
          );
          if (!result?.values?.length) {
            return null;
          }
          try {
            return JSON.parse(result.values[0].value as string) as T;
          } catch (error) {
            console.warn("[PersistentStorage] Failed to parse value from SQLite", {
              key,
              error,
            });
            return null;
          }
        },
        async setItem<T>(key: string, value: T): Promise<void> {
          await dbConnection.run(
            `INSERT OR REPLACE INTO ${SQLITE_TABLE} (key, value) VALUES (?, ?)`,
            [key, JSON.stringify(value)]
          );
        },
        async removeItem(key: string): Promise<void> {
          await dbConnection.run(
            `DELETE FROM ${SQLITE_TABLE} WHERE key = ?`,
            [key]
          );
        },
        async clear(): Promise<void> {
          await dbConnection.execute(`DELETE FROM ${SQLITE_TABLE}`);
        },
      };
    }
  } catch (error) {
    console.warn(
      "[PersistentStorage] Unable to initialize Capacitor Community SQLite, falling back to Preferences",
      error
    );
  }

  const preferencesModule = await import("@capacitor/preferences");
  const Preferences = preferencesModule.Preferences;
  const prefix = "offline:";

  return {
    async getItem<T>(key: string): Promise<T | null> {
      const result = await Preferences.get({ key: `${prefix}${key}` });
      if (!result.value) return null;
      try {
        return JSON.parse(result.value) as T;
      } catch (error) {
        console.warn("[PersistentStorage] Failed to parse Preferences value", {
          key,
          error,
        });
        return null;
      }
    },
    async setItem<T>(key: string, value: T): Promise<void> {
      const payload = {
        key: `${prefix}${key}`,
        value: JSON.stringify(value),
      };
      const schedule =
        typeof window !== "undefined" && typeof (window as any).requestIdleCallback === "function"
          ? (cb: () => void) => (window as any).requestIdleCallback(cb, { timeout: 250 })
          : typeof window !== "undefined"
            ? (cb: () => void) => window.setTimeout(cb, 0)
            : (cb: () => void) => cb();
      schedule(() => {
        void Preferences.set(payload).catch((error) => {
          console.warn("[PersistentStorage] Preferences.set failed", {
            key: payload.key,
            error,
          });
        });
      });
    },
    async removeItem(key: string): Promise<void> {
      await Preferences.remove({ key: `${prefix}${key}` });
    },
    async clear(): Promise<void> {
      const { keys } = await Preferences.keys();
      const offlineKeys = keys.filter((k) => k.startsWith(prefix));
      await Promise.all(offlineKeys.map((k) => Preferences.remove({ key: k })));
    },
  };
};
